/**
 * Layout Component
 *
 * Main application layout wrapper that includes Navbar, Sidebar, and content area.
 * Provides responsive layout with collapsible sidebar for mobile devices.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';

// =============================================================================
// Types
// =============================================================================

interface LayoutProps {
  /** Page content to render in the main area */
  children: ReactNode;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Main application layout component
 *
 * Provides the overall page structure with:
 * - Fixed top navigation bar
 * - Collapsible sidebar navigation
 * - Main content area with proper spacing
 */
export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  /**
   * Toggle sidebar visibility
   */
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  /**
   * Close sidebar (used for mobile navigation)
   */
  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-discord-darkest">
      {/* Top Navigation Bar */}
      <Navbar
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={handleToggleSidebar}
      />

      {/* Sidebar Navigation */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
      />

      {/* Main Content Area */}
      <main
        className="min-h-screen pt-16 transition-all duration-300 lg:pl-64"
        id="main-content"
      >
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
}

export default Layout;
